import type { WatchlistItem } from '../types';
import { parseDirectionFromSearchQuery } from './parse-search-query';
import type { ParsedDirection } from './parse-search-query';

export type FavoriteGroup = {
  direction: ParsedDirection;
  items: WatchlistItem[];
  savedSearches: WatchlistItem[];
  savedTickets: WatchlistItem[];
  minPrice: string | null;
  currency: string;
};

export type DirectionCard = ParsedDirection & {
  count: number;
  savedSearchesCount: number;
  savedTicketsCount: number;
  minPrice: string | null;
  currency: string;
};

function splitWatchlistItems(items: WatchlistItem[]) {
  const savedTickets = items.filter(item =>
    Boolean(item.offer?.flights?.length),
  );
  const savedSearches = items.filter(item => !item.offer?.flights?.length);
  return { savedSearches, savedTickets };
}

export function groupByDirection(items: WatchlistItem[]): FavoriteGroup[] {
  const map = new Map<string, FavoriteGroup>();

  for (const item of items) {
    const direction = parseDirectionFromSearchQuery(item.search_query);
    if (!direction) continue;

    const existing = map.get(direction.key);

    if (existing) {
      const currentMin = existing.minPrice
        ? parseFloat(existing.minPrice)
        : Infinity;
      const candidate = parseFloat(item.initial_price);
      const isCheaper = !Number.isNaN(candidate) && candidate < currentMin;

      map.set(direction.key, {
        ...existing,
        items: [...existing.items, item],
        minPrice: isCheaper ? item.initial_price : existing.minPrice,
        currency: isCheaper ? item.currency : existing.currency,
      });
    } else {
      map.set(direction.key, {
        direction,
        items: [item],
        savedSearches: [],
        savedTickets: [],
        minPrice: item.initial_price || null,
        currency: item.currency,
      });
    }
  }

  return Array.from(map.values()).map(group => {
    const { savedSearches, savedTickets } = splitWatchlistItems(group.items);
    return { ...group, savedSearches, savedTickets };
  });
}

export function toDirectionCards(groups: FavoriteGroup[]): DirectionCard[] {
  return groups.map(group => ({
    ...group.direction,
    count: group.items.length,
    savedSearchesCount: group.savedSearches.length,
    savedTicketsCount: group.savedTickets.length,
    minPrice: group.minPrice,
    currency: group.currency,
  }));
}
